import { useTranslation } from "react-i18next";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Link } from "~/components/ui/link";
import { ProductCard, type ProductCardProduct } from "~/components/ui/product-card";

const PRODUCTS: ProductCardProduct[] = ["drive", "view", "go"];

export default function Products() {
  const { t } = useTranslation("home");
  const { t: tCommon } = useTranslation("common");

  // The cards render once per breakpoint container below (CSS hides all but one), so each set gets its own scope —
  // otherwise every CTA's data-sid would repeat three times on the page.
  const renderCards = (scope: string) =>
    PRODUCTS.map((product) => (
      <ProductCard
        key={product}
        scope={scope}
        product={product}
        subtitle={t(`products.${product}_badge`)}
        ctaLabel={t("products.cta")}
      />
    ));

  return (
    <section className="bg-white py-10 lg:pt-15 lg:pb-6">
      <div className="wrap flex flex-col items-center gap-8 lg:gap-10 2xl:max-w-[1280px]!">
        <div className="flex flex-col items-center gap-2 text-center sm:gap-3">
          {/* Same fluid clamp technique as Hero, but Products' own smaller type tier. */}
          <h2 className="font-display text-text-heading max-w-[241px] text-[24px] leading-[1.15] font-extrabold sm:max-w-none sm:text-[clamp(1.5rem,4vw,2.25rem)] sm:leading-[1.1]">
            {t("products.heading")}
          </h2>
          <p className="text-text-body-alt sm:text-text-body-secondary max-w-[303px] text-sm leading-5 sm:max-w-none sm:text-[clamp(0.875rem,2vw,1.125rem)] sm:leading-[1.3]">
            {t("products.subtitle")}
          </p>
        </div>

        {/* 32px below cards on mobile, 24px from lg — kept separate from the heading's own gap above */}
        <div className="flex w-full flex-col items-center gap-8 lg:gap-6">
          {/* Stacked, centered, non-scrolling — below sm only. Card width 263px. */}
          <div className="flex w-full flex-col items-center gap-8 sm:hidden">{renderCards("mobile")}</div>

          {/* sm up to lg: left-aligned, bled past wrap's own capped width for real scroll room
              (scroll-row-clip/-right-spacer in app.css — same idea as ReviewsCarousel's carousel). */}
          <div className="hidden sm:block sm:self-start lg:hidden">
            <div className="scroll-row-clip">
              <div className="flex [scrollbar-width:none] items-center gap-4 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden">
                <div className="scroll-row-edge-spacer" />
                {renderCards("tablet")}
                <div className="scroll-row-edge-spacer" />
              </div>
            </div>
          </div>

          {/* Centered, fixed-width (302px), never needs to scroll — lg and up. */}
          <div className="hidden lg:flex lg:w-full lg:justify-center lg:gap-6">{renderCards("desktop")}</div>

          <Button
            variant="link"
            size="sm"
            className="h-auto gap-1 p-0 text-base tracking-[0.16px] underline lg:my-[18px] lg:self-end"
            iconRight={<RiArrowRightSLine aria-hidden className="size-5 lg:size-6" />}
            asChild
          >
            <Link data-sid="home-products-shop" to="/products">{tCommon("nav.shop_all_products")}</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
